// @plugins pwa-update.client.ts
import { watch } from 'vue';
import { useActionToaster } from '~/composables/useActionToaster';

const VERSION_KEY = 'agents-portal-version';
const DISMISSED_SESSION_KEY = 'agents-portal-update-dismissed';

export default defineNuxtPlugin(() => {
  let updateUIPrompted = false;

  const getCurrentVersion = (): string => {
    const config = useRuntimeConfig();

    return (config.public.agentsBuildId as string) || '';
  };

  const checkForVersionUpdate = (): void => {
    const currentVersion = getCurrentVersion();

    if (!currentVersion) {
      return;
    }

    const storedVersion = localStorage.getItem(VERSION_KEY);

    if (!storedVersion) {
      localStorage.setItem(VERSION_KEY, currentVersion);
      return;
    }

    if (storedVersion !== currentVersion) {
      triggerUpdateUI();
    }
  };

  /**
   * Get browser-specific keyboard shortcut for hard refresh
   */
  const getRefreshShortcut = (): string => {
    if (!process.client) return '';

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) return '';

    const isMac = /Mac/i.test(navigator.platform);
    const isFirefox = navigator.userAgent.includes('Firefox');
    const isChrome = navigator.userAgent.includes('Chrome');
    const isSafari = navigator.userAgent.includes('Safari') && !isChrome;

    if (isMac) {
      if (isFirefox || isChrome) return '⌘+Shift+R';
      if (isSafari) return '⌘+Option+R';
      return '';
    }

    if (/Win|Linux/i.test(navigator.platform)) {
      return 'Ctrl+F5';
    }

    return '';
  };

  /**
   * Perform a complete application refresh by:
   * 1. Unregistering service workers
   * 2. Clearing browser caches
   * 3. Reloading the page with a cache-busting parameter
   */
  const performHardRefresh = async (): Promise<void> => {
    try {
      /* eslint-disable no-console */
      console.info('PWA Update: Performing hard refresh');
      /* eslint-enable no-console */

      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map((registration) => registration.unregister()),
        );
        /* eslint-disable no-console */
        console.info('PWA Update: Service workers unregistered');
        /* eslint-enable no-console */
      }

      if ('caches' in window) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((key) => window.caches.delete(key)));
        /* eslint-disable no-console */
        console.info('PWA Update: Caches cleared');
        /* eslint-enable no-console */
      }

      const cacheBuster = Date.now();
      const url = new URL(window.location.href);
      url.searchParams.set('pwa-refresh', cacheBuster.toString());
      window.location.href = url.toString();
    } catch (error) {
      console.error('PWA Update: Failed to perform hard refresh', error);
      window.location.reload();
    }
  };

  const onUpdate = async (): Promise<void> => {
    const version = getCurrentVersion();

    if (version) {
      localStorage.setItem(VERSION_KEY, version);
    }

    sessionStorage.removeItem(DISMISSED_SESSION_KEY);
    updateUIPrompted = false;
    await performHardRefresh();
  };

  const onDismiss = (): void => {
    sessionStorage.setItem(DISMISSED_SESSION_KEY, '1');
  };

  /**
   * Show the update toast when a new build is available.
   * @param force Skip session guards (development testing only)
   */
  const triggerUpdateUI = (force = false): void => {
    if (!force) {
      if (updateUIPrompted) {
        return;
      }

      if (sessionStorage.getItem(DISMISSED_SESSION_KEY)) {
        return;
      }
    }

    updateUIPrompted = true;

    /* eslint-disable no-console */
    console.info('PWA Update: Showing update notification');
    /* eslint-enable no-console */

    const shortcut = getRefreshShortcut();
    const additionalMessage = shortcut
      ? `You can also try ${shortcut} for a complete refresh.`
      : '';

    const actionToaster = useActionToaster();
    actionToaster.add(
      'info',
      'A new version of the application is available.',
      [
        {
          label: 'Update now',
          icon: ['fas', 'arrows-rotate'],
          onClick: onUpdate,
        },
      ],
      'Update Available',
      300 * 1000,
      'pwa-update',
      additionalMessage,
      onDismiss,
    );
  };

  const registerVitePwaUpdateCallback = (): boolean => {
    if (!('viteServiceWorker' in window)) {
      return false;
    }

    // @ts-expect-error - viteServiceWorker is injected by @vite-pwa/nuxt
    window.viteServiceWorker.registerUpdateCallback(() => {
      /* eslint-disable no-console */
      console.info(
        'PWA Update: registerUpdateCallback triggered - new version available',
      );
      /* eslint-enable no-console */
      triggerUpdateUI();
    });

    return true;
  };

  const initializeServiceWorkerListeners = (): void => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    /* eslint-disable no-console */
    console.info('PWA Update Plugin: Initializing service worker event listeners');
    /* eslint-enable no-console */

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'VITE_SW_UPDATE') {
        /* eslint-disable no-console */
        console.info('PWA Update: New content is available', event.data);
        /* eslint-enable no-console */
        triggerUpdateUI();
      }
    });

    if (!registerVitePwaUpdateCallback()) {
      const retryInterval = window.setInterval(() => {
        if (registerVitePwaUpdateCallback()) {
          window.clearInterval(retryInterval);
        }
      }, 100);

      window.setTimeout(() => window.clearInterval(retryInterval), 5000);
    }

    navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        if (!registration) {
          /* eslint-disable no-console */
          console.info('PWA Update: No service worker registration found');
          /* eslint-enable no-console */
          return;
        }

        /* eslint-disable no-console */
        console.info('PWA Update: Service worker registration found', {
          scope: registration.scope,
          active: !!registration.active,
          waiting: !!registration.waiting,
        });
        /* eslint-enable no-console */

        if (registration.waiting) {
          /* eslint-disable no-console */
          console.info('PWA Update: Found waiting service worker');
          /* eslint-enable no-console */
          triggerUpdateUI();
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;

          if (!newWorker) {
            return;
          }

          newWorker.addEventListener('statechange', () => {
            if (
              newWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              /* eslint-disable no-console */
              console.info(
                'PWA Update: New service worker installed and waiting',
              );
              /* eslint-enable no-console */
              triggerUpdateUI();
            }
          });
        });
      })
      .catch((error) => {
        console.error(
          'PWA Update: Error checking service worker registration',
          error,
        );
      });
  };

  if (process.client) {
    checkForVersionUpdate();

    const nuxtApp = useNuxtApp();
    watch(
      () => nuxtApp.$pwa?.needRefresh,
      (needRefresh) => {
        if (needRefresh) {
          triggerUpdateUI();
        }
      },
      { immediate: true },
    );

    const config = useRuntimeConfig();
    const exposeTestHelpers =
      process.env.NODE_ENV !== 'production' ||
      config.public.appEnv === 'staging';

    if (exposeTestHelpers) {
      /* eslint-disable no-console */
      console.info(
        'PWA Update: Exposing test function as window.__testPwaUpdate(force)',
      );
      /* eslint-enable no-console */

      // @ts-expect-error - development/staging testing helper
      window.__testPwaUpdate = (force = true) => triggerUpdateUI(force);
      // @ts-expect-error - development/staging testing helper
      window.__testPwaToastOnly = () => triggerUpdateUI(true);
    }

    initializeServiceWorkerListeners();
  }
});
